'use client';

import { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft, FileText, Sparkles, Zap } from 'lucide-react';

interface WelcomeScreen {
  heading: string;
  icon: typeof FileText;
  body: React.ReactNode;
}

const SCREENS: WelcomeScreen[] = [
  {
    heading: 'Never Miss a Conversation',
    icon: FileText,
    body: (
      <>
        <p className="text-base">
          Insurance agencies handle hundreds of client conversations daily. Without proper
          logging, <strong>important details get lost</strong>, conversations go undocumented,
          and when an E&amp;O claim arises, you&rsquo;re left scrambling for records.
        </p>
        <p className="text-[15px]">
          Manual copy-paste logging is slow, error-prone, and guaranteed to miss messages.
          Your team shouldn&rsquo;t have to choose between doing their job and documenting it.
        </p>
        <p className="text-[15px]">
          Voley&rsquo;s <strong>Automated Smart-Logging</strong> captures every conversation
          and logs it directly to your AMS with full context &mdash; <strong>timestamps,
          sender names, and complete message history</strong>. Zero manual effort.
        </p>
      </>
    ),
  },
  {
    heading: 'The Most Complete Logging',
    icon: Sparkles,
    body: (
      <>
        <p className="text-base">
          No other platform logs insurance conversations like Voley:
        </p>
        <div className="flex flex-col gap-2.5 pl-2">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">1</div>
            <span className="text-[15px]"><strong>Timestamped entries</strong> with sender names on every message</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">2</div>
            <span className="text-[15px]"><strong>Text or Link format</strong> &mdash; searchable in your AMS or a clean reference</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">3</div>
            <span className="text-[15px]"><strong>Follow-up tasks</strong> created directly in your AMS client profile</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">4</div>
            <span className="text-[15px]"><strong>MMS &amp; attachments</strong> auto-uploaded alongside the log</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">5</div>
            <span className="text-[15px]"><strong>Smart formatting</strong> that adapts to conversation length</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">6</div>
            <span className="text-[15px]"><strong>Automatic end-of-day logging</strong> catches anything your team missed</span>
          </div>
        </div>
      </>
    ),
  },
  {
    heading: "Let's Walk Through It",
    icon: Zap,
    body: (
      <>
        <p className="text-base">
          We&rsquo;ll show you the complete logging flow:
        </p>
        <div className="flex flex-col gap-3 pl-4">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">1</div>
            <span className="text-[15px]">How to manually log conversations to your AMS</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">2</div>
            <span className="text-[15px]">Text vs. Link posting &mdash; what your AMS sees</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">3</div>
            <span className="text-[15px]">Tasks, attachments, and auto-logging settings</span>
          </div>
        </div>
      </>
    ),
  },
];

interface LoggingWelcomeModalProps {
  onComplete: () => void;
}

export function LoggingWelcomeModal({ onComplete }: LoggingWelcomeModalProps) {
  const [activeScreen, setActiveScreen] = useState(0);
  const isLastScreen = activeScreen === SCREENS.length - 1;

  const handleNext = useCallback(() => {
    if (isLastScreen) {
      onComplete();
    } else {
      setActiveScreen((prev) => prev + 1);
    }
  }, [isLastScreen, onComplete]);

  const handleBack = useCallback(() => {
    setActiveScreen((prev) => Math.max(0, prev - 1));
  }, []);

  const screen = SCREENS[activeScreen];
  const Icon = screen.icon;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" />

      {/* Modal */}
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
        <div
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 fade-in duration-300"
          role="dialog"
          aria-modal="true"
          aria-label="Logging Tour Welcome"
        >
          {/* Content */}
          <div className="p-8">
            {/* Icon and Heading Row */}
            <div className="flex items-start gap-4 mb-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl shrink-0 bg-accent-blue/[0.08]">
                <Icon className="h-6 w-6 text-accent-blue" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-1 text-gray-900">{screen.heading}</h2>
              </div>
            </div>

            {/* Body */}
            <div className="text-left leading-relaxed mb-8 space-y-4 text-gray-600 ml-16">
              {screen.body}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              {activeScreen > 0 ? (
                <button
                  onClick={handleBack}
                  aria-label="Previous screen"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </button>
              ) : (
                <div />
              )}

              <button
                onClick={handleNext}
                aria-label={isLastScreen ? 'Start the tour' : 'Next screen'}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium text-white bg-accent-blue hover:brightness-110 transition-all shadow-sm hover:shadow-md"
              >
                {isLastScreen ? "Let's Go" : 'Next'}
                {!isLastScreen && <ChevronRight className="h-4 w-4" />}
              </button>
            </div>

            {/* Step dots */}
            <div className="flex justify-center gap-1.5 mt-5">
              {SCREENS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === activeScreen ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
